<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="650px" persistent scrollable>
    <v-card>
      <v-card-title class="headline">
        {{ isEdit ? 'Sửa Cấu Hình Biểu Đồ' : 'Thêm Cấu Hình Biểu Đồ' }}
      </v-card-title>
      <v-card-text class="pt-4">
        <v-form ref="chartForm" v-model="formValid" lazy-validation>
          <v-text-field v-model="form.name" label="Tên biểu đồ" :rules="formRules.name" outlined dense
            placeholder="VD: Thống kê dân số theo tỉnh"></v-text-field>

          <v-select v-model="form.type" :items="chartTypes" label="Loại biểu đồ" :rules="formRules.type" outlined
            dense></v-select>

          <v-autocomplete v-model="form.x_axis_field" :items="layerFields" item-text="name" item-value="code"
            label="Trục Nhãn" outlined dense>
          </v-autocomplete>

          <v-divider class="mb-4"></v-divider>
          <div class="subtitle-2 mb-2">Cấu hình Dữ liệu Trục Giá trị</div>

          <v-radio-group v-model="configMode" row class="mt-0">
            <v-radio label="Cơ bản" value="basic"></v-radio>
            <v-radio label="Nâng cao" value="advanced"></v-radio>
          </v-radio-group>

          <template v-if="configMode === 'basic'">
            <v-autocomplete v-model="form.y_axis_field" :items="numericFields" item-text="name" item-value="code"
              label="Trục Giá trị"
              :rules="[v => configMode !== 'basic' || !!v || 'Vui lòng chọn trường Trục Giá trị']" outlined dense>
            </v-autocomplete>

            <v-select v-model="form.aggregation" :items="aggregations" label="Phép tính" outlined dense></v-select>
          </template>

          <template v-else>
            <v-select :items="formulaTemplates" label="Mẫu công thức (Tùy chọn)" item-text="name" return-object
              outlined dense clearable @change="applyFormulaTemplate" placeholder="Chọn mẫu công thức có sẵn...">
            </v-select>

            <v-textarea v-model="form.custom_formula" ref="customFormulaInput" label="Công thức" rows="4"
              :rules="[v => configMode !== 'advanced' || !!v || 'Vui lòng nhập công thức tuỳ chỉnh']" outlined dense
              placeholder="VD: SUM({dan_so}) / SUM({dien_tich})&#10;VD: SUM(ST_Area({geometry}::geography))"></v-textarea>
            
            <div class="mb-3">
              <small><strong>Hàm tính toán:</strong></small>
              <div class="mt-1">
                <v-chip small outlined color="primary" class="mr-1 mb-1" @click="insertFunction('SUM')">SUM</v-chip>
                <v-chip small outlined color="primary" class="mr-1 mb-1" @click="insertFunction('COUNT')">COUNT</v-chip>
                <v-chip small outlined color="primary" class="mr-1 mb-1" @click="insertFunction('AVG')">AVG</v-chip>
                <v-chip small outlined color="primary" class="mr-1 mb-1" @click="insertFunction('MAX')">MAX</v-chip>
                <v-chip small outlined color="primary" class="mr-1 mb-1" @click="insertFunction('MIN')">MIN</v-chip>
                <span class="mx-1">|</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip small dark color="teal" class="mr-1 mb-1" v-bind="attrs" v-on="on" @click="insertPostgisFunction('ST_Area')">
                      <v-icon left small>mdi-shape-polygon-plus</v-icon> ST_Area
                    </v-chip>
                  </template>
                  <span>Diện tích (m²)</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip small dark color="teal" class="mr-1 mb-1" v-bind="attrs" v-on="on" @click="insertPostgisFunction('ST_Length')">
                      <v-icon left small>mdi-ruler</v-icon> ST_Length
                    </v-chip>
                  </template>
                  <span>Chiều dài (m)</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip small dark color="teal" class="mr-1 mb-1" v-bind="attrs" v-on="on" @click="insertPostgisFunction('ST_Perimeter')">
                      <v-icon left small>mdi-vector-polyline</v-icon> ST_Perimeter
                    </v-chip>
                  </template>
                  <span>Chu vi (m)</span>
                </v-tooltip>
              </div>
            </div>

            <div class="mb-2">
              <small><strong>Biến có thể dùng:</strong></small>
              <v-expansion-panels v-model="openedPanel" flat accordion class="mt-1" style="border: 1px solid #e0e0e0; border-radius: 4px;">
                <v-expansion-panel v-for="(group, groupName) in groupedLayerFields" :key="groupName">
                  <v-expansion-panel-header style="min-height: 40px; padding: 0 12px;">
                    <span class="text-caption font-weight-medium">{{ groupName }} ({{ group.length }})</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-chip v-for="field in group" :key="field.code" small 
                      :color="field.type === 'array' ? 'grey lighten-2' : ''" 
                      :text-color="field.type === 'array' ? 'grey' : ''"
                      class="mr-1 mb-1"
                      :style="field.type === 'array' ? 'cursor: not-allowed;' : ''"
                      @click="field.type !== 'array' && insertVariable(field.code)">
                      {{ '{' + field.code + '}' }}
                      <v-tooltip v-if="field.type === 'array'" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-alert-circle-outline</v-icon>
                        </template>
                        <span>Trường dạng mảng (JSON), không dùng cho phép toán trực tiếp</span>
                      </v-tooltip>
                    </v-chip>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </template>

          <!-- Khung hiển thị kết quả Kiểm Tra Dữ Liệu -->
          <v-alert ref="validationAlert" v-if="validationResult"
            :type="validationResult.valid ? 'success' : 'warning'" dismissible text class="mt-3 mb-0">
            <div>
              <strong>{{ validationResult.valid ? 'Dữ liệu hợp lệ:' : 'Cảnh báo dữ liệu:' }}</strong>
              {{ validationResult.valid ? validationResult.message : validationResult.error }}
            </div>
          </v-alert>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn color="info" outlined :loading="validating" @click="validateForm">
          <v-icon left small>mdi-check-decagram</v-icon>
          Kiểm tra dữ liệu
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Hủy</v-btn>
        <v-btn color="primary" :loading="saving" @click="saveConfig">Lưu Cấu Hình</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ConfirmBuilder } from '@utils'
import { sdk } from '@service/http/axios'

export default {
  name: 'ChartSettingsForm',
  props: {
    value: { type: Boolean, default: false },
    layerId: { type: [String, Number], required: true },
    layerFields: { type: Array, default: () => [] },
    isEdit: { type: Boolean, default: false },
    editId: { type: [String, Number], default: null },
    initialData: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      saving: false,
      validating: false,
      validationResult: null,
      configMode: 'basic',
      formValid: true,
      form: {
        name: '',
        type: 'bar',
        x_axis_field: '',
        y_axis_field: '',
        aggregation: 'SUM',
        custom_formula: '',
        is_active: true,
      },
      formRules: {
        name: [v => !!v || 'Vui lòng nhập tên biểu đồ'],
        type: [v => !!v || 'Vui lòng chọn loại biểu đồ'],
      },
      chartTypes: [
        { text: 'Biểu đồ Đường (Line)', value: 'line' },
        { text: 'Biểu đồ Cột (Bar)', value: 'bar' },
        { text: 'Biểu đồ Tròn (Pie)', value: 'pie' },
      ],
      aggregations: [
        { text: 'Tổng (SUM)', value: 'SUM' },
        { text: 'Đếm (COUNT)', value: 'COUNT' },
        { text: 'Trung bình (AVG)', value: 'AVG' },
        { text: 'Giá trị lớn nhất (MAX)', value: 'MAX' },
        { text: 'Giá trị nhỏ nhất (MIN)', value: 'MIN' }
      ],
      openedPanel: 0,
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.validationResult = null
        if (this.isEdit) {
          this.form = { ...this.initialData }
          this.configMode = this.initialData.custom_formula ? 'advanced' : 'basic'
        } else {
          this.form = { name: '', type: 'bar', x_axis_field: '', y_axis_field: '', aggregation: 'SUM', custom_formula: '', is_active: true }
          this.configMode = 'basic'
        }
        if (this.$refs.chartForm) this.$refs.chartForm.resetValidation()
      }
    }
  },
  computed: {
    layerGeomField() {
      const geomField = this.layerFields.find(f => f.type === 'geometry' || ['geometry', 'shape', 'geom'].includes(f.code.toLowerCase()));
      return geomField ? geomField.code : 'geometry';
    },
    formulaTemplates() {
      const geom = this.layerGeomField;
      return [
        { name: 'Tỷ lệ diện tích khai báo/thực tế', formula: `(SUM({TongDienTich}) / (SUM(ST_Area({${geom}}::geography)) / 10000)) * 100` },
        { name: 'Tổng diện tích thực tế (ha)', formula: `SUM(ST_Area({${geom}}::geography)) / 10000` },
        { name: 'Tổng chu vi (km)', formula: `SUM(ST_Perimeter({${geom}}::geography)) / 1000` },
        { name: 'Mật độ (đơn vị/ha)', formula: `COUNT({MaDinhDanh}) / (SUM(ST_Area({${geom}}::geography)) / 10000)` },
      ]
    },
    numericFields() {
      return this.layerFields;
    },
    groupedLayerFields() {
      const groups = {
        'Số liệu chính / Trường đơn': [],
      }
      
      this.layerFields.forEach(field => {
        if (field.code.includes('_')) {
          const prefix = field.code.split('_')[0]
          let groupName = prefix
          
          if (!groups[groupName]) groups[groupName] = []
          groups[groupName].push(field)
        } else {
          groups['Số liệu chính / Trường đơn'].push(field)
        }
      })
      
      const sortedGroups = {}
      if (groups['Số liệu chính / Trường đơn'].length > 0) {
        sortedGroups['Số liệu chính / Trường đơn'] = groups['Số liệu chính / Trường đơn']
      }
      
      Object.keys(groups).sort().forEach(key => {
        if (key !== 'Số liệu chính / Trường đơn' && groups[key].length > 0) {
          sortedGroups[key] = groups[key]
        }
      })
      
      return sortedGroups
    },
  },
  methods: {
    async validateForm() {
      if (!this.$refs.chartForm.validate()) return

      this.validating = true
      this.validationResult = null

      let payload = { ...this.form }
      if (this.configMode === 'basic') {
        payload.custom_formula = null
      } else {
        payload.y_axis_field = null
        payload.aggregation = null
      }

      try {
        const { data } = await sdk.request({
          url: `layer/${this.layerId}/chart-configs/validate`,
          method: 'post',
          data: payload
        })
        this.validationResult = data
      } catch (e) {
        const errorText = e.response?.data?.error || e.response?.data?.message || 'Lỗi kiểm tra dữ liệu'
        this.validationResult = { valid: false, error: errorText }
      } finally {
        this.validating = false
        await this.$nextTick()
        if (this.$refs.validationAlert) {
          const el = this.$refs.validationAlert.$el || this.$refs.validationAlert
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
      }
    },
    async saveConfig() {
      if (!this.$refs.chartForm.validate()) return

      let payload = { ...this.form }
      if (this.configMode === 'basic') {
        payload.custom_formula = null
      } else {
        payload.y_axis_field = null
        payload.aggregation = null
      }

      this.saving = true
      try {
        if (this.isEdit) {
          await sdk.request({
            url: `layer/${this.layerId}/chart-configs/${this.editId}`,
            method: 'put',
            data: payload
          })
          this.$store.dispatch('notify/success', { message: 'Đã cập nhật cấu hình biểu đồ thành công' })
        } else {
          await sdk.request({
            url: `layer/${this.layerId}/chart-configs`,
            method: 'post',
            data: payload
          })
          this.$store.dispatch('notify/success', { message: 'Đã tạo cấu hình biểu đồ mới thành công' })
        }
        this.$emit('input', false)
        this.$emit('saved')
      } catch (e) {
        this.$store.dispatch('notify/error', { message: e.response?.data?.message || e.message })
      } finally {
        this.saving = false
      }
    },
    insertVariable(code) {
      this.form.custom_formula = (this.form.custom_formula || '') + `{${code}}`
    },
    insertFunction(funcName) {
      this.form.custom_formula = (this.form.custom_formula || '') + `${funcName}()`
    },
    insertPostgisFunction(funcName) {
      this.form.custom_formula = (this.form.custom_formula || '') + `${funcName}({${this.layerGeomField}}::geography)`
    },
    async applyFormulaTemplate(template) {
      if (!template) return
      if (this.form.custom_formula) {
        let confirm = new ConfirmBuilder()
        confirm
          .addMessage('Bạn có chắc muốn ghi đè công thức hiện tại bằng mẫu này không?')
          .setTitle('Xác nhận ghi đè')
          .setType('warning')

        if (await confirm.confirm()) {
          this.form.custom_formula = template.formula
        }
      } else {
        this.form.custom_formula = template.formula
      }
    },
  },
}
</script>
